import type { ServiceRequestQuotePublic } from "@openings/api-contract";
import type { DbDateString } from "@openings/api-client";

import type { QuoteBookingClientProps } from "./QuoteBookingClient";

type QuoteScheduleSummary = {
  title?: string;
  address?: string;
};

type QuoteCancellationPolicy = {
  enabled?: boolean;
  hours?: number | null;
  note?: string | null;
  lateGraceMinutes?: number | null;
};

/**
 * Single mapping from the public quote-link contract into the booking client.
 * The dev harness uses this same path so quoted duration and member-schedule
 * resolution cannot drift from the live link.
 */
export function buildQuoteBookingClientProps(input: {
  quote: ServiceRequestQuotePublic;
  schedule?: QuoteScheduleSummary;
  cancellationPolicy?: QuoteCancellationPolicy;
  /**
   * The business's customer booking window, which applies to quotes too.
   * `timeZone` is the business's, used only when an older quote payload has
   * no `bookingTimeZone`.
   */
  bookingWindow?: { horizonDays?: number | null; timeZone?: string | null };
  initialDate?: DbDateString;
}): QuoteBookingClientProps {
  const { quote, schedule, cancellationPolicy, bookingWindow, initialDate } =
    input;
  const cancellationPolicyEnabled = cancellationPolicy?.enabled === true;
  // Quote send endpoints persist an omitted price as zero. Older/mixed API
  // builds may still expose the service's DEPOSIT/FULL policy for that quote,
  // but a zero total never requires a payment method server-side.
  const paymentRequirement =
    quote.quotePrice > 0 ? (quote.quotePaymentRequirement ?? "NONE") : "NONE";

  return {
    salonId: quote.salonId,
    requestId: quote.requestId,
    quotePublicId: quote.quotePublicId,
    teamMemberId: quote.quoteTeamMemberId,
    teamMemberName: quote.quoteTeamMemberName ?? undefined,
    scheduleId: quote.quoteScheduleId,
    bookingScheduleId: quote.bookingScheduleId ?? quote.quoteScheduleId,
    scheduleTitle: schedule?.title,
    scheduleAddress: schedule?.address,
    serviceId: quote.quoteServiceId,
    serviceTitle: quote.quoteServiceTitle,
    price: quote.quotePrice,
    durationMinutes: quote.quoteDuration,
    // Older API builds omit the policy; treat that as no upfront payment so
    // the flow never demands a card the server would not charge.
    paymentRequirement,
    depositAmount:
      paymentRequirement === "DEPOSIT"
        ? (quote.quoteDepositAmount ?? null)
        : null,
    initialBookingState:
      quote.bookingState ??
      (quote.paymentPending
        ? "PAYMENT_REVIEW"
        : quote.status === "APPOINTMENT_SCHEDULED"
          ? "CONFIRMED"
          : "AVAILABLE"),
    // Unknown (older payload) stays undefined: the payment step then states
    // the stricter windowed policy, never an unconditional refund.
    cancellationPolicyEnabled: cancellationPolicy?.enabled,
    cancellationPolicyHours: cancellationPolicyEnabled
      ? (cancellationPolicy?.hours ?? null)
      : null,
    cancellationPolicyNote: cancellationPolicyEnabled
      ? (cancellationPolicy?.note ?? null)
      : null,
    lateGraceMinutes: cancellationPolicyEnabled
      ? (cancellationPolicy?.lateGraceMinutes ?? null)
      : null,
    bookingHorizonDays: bookingWindow?.horizonDays ?? null,
    // Server-resolved, parent location first: the stored schedule can be a
    // member child whose own zone is stale (cross-review r3).
    bookingTimeZone: quote.bookingTimeZone || bookingWindow?.timeZone || null,
    // The quoted service's minimum notice; older payloads omit it and the
    // API still refuses a time inside it.
    minBookingNoticeMinutes: quote.minBookingNoticeMinutes ?? null,
    initialDate,
  };
}

/**
 * The cancellation-policy fields the quote hands to the shared appointment
 * form. The payment step's refund line reads `cancellationPolicyEnabled`, so
 * dropping it would turn a disabled window into a six-hour promise.
 */
export function quoteFormPolicy(
  props: Pick<
    QuoteBookingClientProps,
    | "cancellationPolicyEnabled"
    | "cancellationPolicyHours"
    | "cancellationPolicyNote"
    | "lateGraceMinutes"
  >,
) {
  return {
    cancellationPolicyEnabled: props.cancellationPolicyEnabled,
    cancellationPolicyHours: props.cancellationPolicyHours,
    cancellationPolicyNote: props.cancellationPolicyNote,
    lateGraceMinutes: props.lateGraceMinutes,
  };
}
